import _ from 'lodash';
import { elementTypes } from 'crazyEditor/components/Elements/index';
import { SPACE_BETWEEN_MOBILE_ELEMENTS, CONTAINING_MOBILE_ELEMENT_BOUNDRIES, CONTAINED_ELEMENT_OVERFLOW_TOLERANCE, MOBILE_MIN_HEIGHT } from 'crazyEditor/core/constants/editorConstants';
import { PAGE_ELEMENT_ID } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { getHiddenElements, getVisibleElements } from 'crazyEditor/core/elements/utils/elementFilterUtils';

export function getElementRect(element) {
  return {
    top: element.layout.y,
    bottom: element.layout.y + element.layout.height,
    left: element.layout.x,
    right: element.layout.x + element.layout.width,
  };
}

function checkIfElementIsContained(containingElementRect, containedElementRect, overflowTolerance) {
  const containedElementOverflowTolerance = overflowTolerance ? overflowTolerance : 0;
  const containedElementRectWidthTolerance = (containedElementRect.right - containedElementRect.left) * containedElementOverflowTolerance;
  const containedElementRectHeightTolerance = (containedElementRect.bottom - containedElementRect.top) * containedElementOverflowTolerance;
  return !(
    containedElementRect.left < containingElementRect.left - containedElementRectWidthTolerance ||
    containedElementRect.right > containingElementRect.right + containedElementRectWidthTolerance ||
    containedElementRect.top < containingElementRect.top - containedElementRectHeightTolerance ||
    containedElementRect.bottom > containingElementRect.bottom + containedElementRectHeightTolerance
  );
}

function isElementContainedAlready(containingElementsTree, element) {
  let contained = false;
  _.forEach(containingElementsTree, (containingElement) => {
    if (containingElement.containedElements.allContainedElements[element.id]) {
      contained = true;
      return false;
    }
  });
  return contained;
}

function getContainedElements(elements, element) {
  const elementRect = getElementRect(element);
  const containedElements = [];
  const structuredContainedElements = {};
  let treatedElements = {};
  _.forEach(elements, (candidate) => {
    if (element !== candidate && checkIfElementIsContained(elementRect, getElementRect(candidate), CONTAINED_ELEMENT_OVERFLOW_TOLERANCE)) {
      containedElements.push(candidate);
    }
  });
  _.forEach(containedElements, (containedElement) => {
    if (!treatedElements[containedElement.id]) {
      const recursiveContainedElements = getContainedElements(containedElements, containedElement);
      treatedElements = _.merge({}, treatedElements, recursiveContainedElements.allContainedElements);
      treatedElements[containedElement.id] = containedElement;
      structuredContainedElements[containedElement.id] = _.merge({}, containedElement, {containedElements: recursiveContainedElements});
    }
  });
  return {structuredContainedElements: structuredContainedElements, allContainedElements: treatedElements};
}

export function getContainingElementsTree(sortedDesktopElements) {
  const containingElementsTree = {};
  _.forEach(sortedDesktopElements, (element) => {
    if (element.type === elementTypes.ShapeElement && !isElementContainedAlready(containingElementsTree, element)) {
      const containedElements = getContainedElements(sortedDesktopElements, element);
      if (!_.isEmpty(containedElements.structuredContainedElements)) {
        containingElementsTree[element.id] = _.merge({}, element, {containedElements: containedElements});
      }
    }
  });
  return containingElementsTree;
}

export function getContainedElementContainingElement(allContainingElements, containedElement) {
  let containingElement = null;
  _.forEach(allContainingElements, (element) => {
    if (_.find(element.containedElements.structuredContainedElements, ['id', containedElement.id])) {
      containingElement = element;
      return false;
    }
  });
  return containingElement;
}

export function getContainedElementContainingElements(allContainingElements, containedElement) {
  const containingElements = {};
  _.forEach(allContainingElements, (element) => {
    if (_.find(element.containedElements.allContainedElements, ['id', containedElement.id])) {
      containingElements[element.id] = element;
    }
  });
  return containingElements;
}

export function getAllContainedElements(containingElementsTree) {
  let allContainedElements = {};
  _.forEach(containingElementsTree, (containingElement) => {
    allContainedElements = _.merge({}, allContainedElements, containingElement.containedElements.allContainedElements);
  });
  return allContainedElements;
}

export function getAllContainingElements(containingElementsTree) {
  let allContainingElements = {};
  _.forEach(containingElementsTree, (containingElement) => {
    if (!_.isEmpty(containingElement.containedElements.structuredContainedElements)) {
      allContainingElements[containingElement.id] = containingElement;
      allContainingElements = _.merge({}, allContainingElements, getAllContainingElements(containingElement.containedElements.structuredContainedElements));
    }
  });

  return allContainingElements;
}

function buildSortedElementsTopAndBottomEdges(mobileElements) {
  const elementsTopAndBottomEdges = [];
  _.forEach(mobileElements, (mobileElement) => {
    elementsTopAndBottomEdges.push({position: mobileElement.layout.y, element: mobileElement, isTop: true});
    elementsTopAndBottomEdges.push({position: mobileElement.layout.y + mobileElement.layout.height, element: mobileElement, isTop: false});
  });
  return _.sortBy(elementsTopAndBottomEdges, ['position']);
}

export function getUpdatedMobileElementsAfterDelete(state, mobileIdsToDelete, pageElement) {
  const sortedElementsTopAndBottomEdges = buildSortedElementsTopAndBottomEdges(getVisibleElements(state.mobile.elements));
  const sortedDesktopElements = _.sortBy(state.desktop.elements, ['layout.y', 'layout.x']);
  const containingElementsTree = getContainingElementsTree(sortedDesktopElements);
  const allContainingElements = getAllContainingElements(containingElementsTree);
  const allContainedElements = getAllContainedElements(containingElementsTree);
  const updatedPositionMobileElements = {
    elements: getHiddenElements(state.mobile.elements),
    staticElements: {}
  };
  let moveElementUpBy = 0;
  let previousEdgePosition = 0;
  let deletionsInProcess = [];

  _.forEach(sortedElementsTopAndBottomEdges, (edge) => {
    const containingElement = _.find(allContainingElements, ['id', edge.element.desktopId]);
    if (edge.isTop) {
      if (_.includes(mobileIdsToDelete, edge.element.id)) {
        updatedPositionMobileElements.elements[edge.element.id] = edge.element;
        if (containingElement) {
          moveElementUpBy += CONTAINING_MOBILE_ELEMENT_BOUNDRIES;
        } else {
          deletionsInProcess.push(_.merge({}, edge, {deletion: true}));
        }
      } else {
        const lastDeletionInProcess = _.last(deletionsInProcess);
        if (lastDeletionInProcess && lastDeletionInProcess.deletion) {
          moveElementUpBy += edge.position - previousEdgePosition;
        }
        updatedPositionMobileElements.elements[edge.element.id] = _.merge({}, edge.element, {layout: {y: edge.element.layout.y - moveElementUpBy}});
        if (!containingElement) {
          deletionsInProcess.push(_.merge({}, edge, {deletion: false}));
        }
      }
    } else {
      deletionsInProcess = _.filter(deletionsInProcess, (deletion) => deletion.element.id !== edge.element.id);
      if (_.includes(mobileIdsToDelete, edge.element.id)) {
        let shortenContainingElementBy = 0;
        if (containingElement) {
          shortenContainingElementBy = 2 * CONTAINING_MOBILE_ELEMENT_BOUNDRIES;
          moveElementUpBy += CONTAINING_MOBILE_ELEMENT_BOUNDRIES;
        } else {
          const lastDeletionInProcess = _.last(deletionsInProcess);
          if (!lastDeletionInProcess || lastDeletionInProcess.deletion) {
            shortenContainingElementBy = edge.position - previousEdgePosition;
            moveElementUpBy += shortenContainingElementBy;
          }
        }
        let containedElement = _.find(allContainedElements, ['id', edge.element.desktopId]);
        while (containedElement) {
          const containedElementContainingElement = getContainedElementContainingElement(allContainingElements, containedElement);
          const containingUpdatedPositionMobileElement = _.find(updatedPositionMobileElements.elements, ['desktopId', containedElementContainingElement.id]);
          if (containingUpdatedPositionMobileElement) {
            updatedPositionMobileElements.elements[containingUpdatedPositionMobileElement.id] = _.merge({}, containingUpdatedPositionMobileElement, {layout: {height: containingUpdatedPositionMobileElement.layout.height - shortenContainingElementBy}});
            containedElement = _.find(allContainedElements, ['id', containedElementContainingElement.id]);
          } else {
            // this can happen if the elements are contained in desktop but the contained element is above the containing element in mobile
            containedElement = null;
          }
        }
      }
    }
    previousEdgePosition = edge.position;
  });

  updatedPositionMobileElements.staticElements[pageElement.id] = {
    layout: {
      height: {
        $set: Math.max(pageElement.layout.height - moveElementUpBy, MOBILE_MIN_HEIGHT)
      }
    }
  };
  return updatedPositionMobileElements;
}
